/**
 * A-05 — staff accounts (pharmacist / admin / support): create + signup-link, edit, reset
 * password, unlock, suspend.
 *
 * The list is a table (components/ui/VirtualTable) that keeps its controls in view: search by
 * name, email or phone; filter by role and status; sort (+ direction); and a batch size for the
 * pages loaded as you scroll. The API returns each role's accounts whole, so the search, filters,
 * sort and paging run in the browser (lib/clientList.ts).
 */

import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import {
  activateStaff,
  createStaff,
  listStaff,
  reissueSignupLink,
  resetStaffPassword,
  suspendStaff,
  unlockStaff,
  updateStaff,
} from "../../api/admin";
import type { SignupLink, Staff as StaffAccount } from "../../api/admin";
import { describeError } from "../../api/errors";
import { useIdempotencyKey } from "../../api/useIdempotencyKey";
import { ResetPasswordForm } from "../../components/ResetPasswordForm";
import { ExportButton } from "../../components/ui/ExportButton";
import {
  BatchSizeFilter,
  ClearFilters,
  FilterBar,
  SearchBox,
  SelectFilter,
  SortFilter,
} from "../../components/ui/FilterBar";
import {
  EditIcon,
  IconButton,
  KeyIcon,
  LinkIcon,
  PauseIcon,
  PlayIcon,
  RowActions,
  UnlockIcon,
} from "../../components/ui/IconButton";
import { Modal } from "../../components/ui/Modal";
import { TableRow, VirtualTable } from "../../components/ui/VirtualTable";
import { signupLinkSent, toastFailure } from "../../lib/adminFeedback";
import { narrowAndSort, useClientPages } from "../../lib/clientList";
import type { SortValue } from "../../lib/clientList";
import { phoneLabel } from "../../lib/phone";
import type { TableColumn } from "../../lib/tableColumns";
import { sheetDateTime, sheetPhone, type ExportColumn } from "../../lib/tableExport";
import { EditStaffForm, StaffForm } from "./StaffForm";
import type { StaffEditValues, StaffFormValues } from "./StaffForm";

const ROLES = ["pharmacist", "admin", "support"] as const;
type Role = (typeof ROLES)[number];
const ROLE_LABEL: Record<string, string> = { pharmacist: "Pharmacist", admin: "Admin", support: "Support" };
const ROLE_OPTIONS: { value: "" | Role; label: string }[] = [
  { value: "", label: "All roles" },
  ...ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] })),
];

const STATUS_LABEL: Record<string, string> = {
  active: "Active",
  pending_verification: "Pending",
  locked: "Locked",
  suspended: "Suspended",
  revoked: "Revoked",
};
const STATUS_OPTIONS = [{ value: "", label: "All" }, ...Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))];
const STATUS_TONE: Record<string, string> = {
  active: "bg-emerald-50 text-emerald-700",
  locked: "bg-amber-50 text-amber-700",
  suspended: "bg-red-50 text-red-700",
  revoked: "bg-red-50 text-red-700",
};

type SortKey = "created_at" | "name" | "email" | "role" | "status";
const SORTS: { value: SortKey; label: string }[] = [
  { value: "created_at", label: "Created" },
  { value: "name", label: "Name" },
  { value: "email", label: "Email" },
  { value: "role", label: "Role" },
  { value: "status", label: "Status" },
];
const SORT_VALUE: Record<SortKey, (s: StaffAccount) => SortValue> = {
  created_at: (s) => s.created_at,
  name: (s) => s.full_name,
  email: (s) => s.email,
  role: (s) => s.role,
  status: (s) => s.status,
};
// The direction each sort reads most naturally in, applied when the sort changes.
const NATURAL_DESC: Record<SortKey, boolean> = { created_at: true, name: false, email: false, role: false, status: false };

// Contact (email and phone) from md and role from sm; narrower screens show them under the name.
// On a phone the actions wrap two by two; from sm, four to a line (as on the Doctors list).
const COLUMNS: TableColumn[] = [
  { key: "name", header: "Name", width: "minmax(0,1fr)" },
  { key: "role", header: "Role", width: "6.5rem", from: "sm" },
  { key: "contact", header: "Contact", width: "minmax(0,1.3fr)", from: "md" },
  { key: "status", header: "Status", width: "5.75rem" },
  { key: "actions", header: "Actions", width: { base: "4.25rem", sm: "9.25rem" }, align: "end" },
];

const EXPORT_COLUMNS: ExportColumn<StaffAccount>[] = [
  { header: "Name", value: (s) => s.full_name },
  { header: "Role", value: (s) => ROLE_LABEL[s.role] ?? s.role },
  { header: "Phone", value: (s) => sheetPhone(s.phone) },
  { header: "Email", value: (s) => s.email },
  { header: "Status", value: (s) => STATUS_LABEL[s.status] ?? s.status },
  { header: "Must change password", value: (s) => s.must_change_password },
  { header: "Created (IST)", value: (s) => sheetDateTime(s.created_at) },
];

export function Staff() {
  const qc = useQueryClient();
  // Every role's accounts; the role is one of the filters below.
  const accounts = useQueries({
    queries: ROLES.map((role) => ({ queryKey: ["staff", role], queryFn: () => listStaff(role) })),
    combine: (results) => ({
      rows: results.flatMap((r) => r.data ?? []),
      loading: results.some((r) => r.isLoading),
      error: results.find((r) => r.error)?.error ?? null,
      refetch: () => results.forEach((r) => void r.refetch()),
    }),
  });
  const [formOpen, setFormOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [link, setLink] = useState<SignupLink | null>(null);

  // list controls
  const [q, setQ] = useState("");
  const [role, setRole] = useState<"" | Role>("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState<SortKey>("created_at");
  const [descending, setDescending] = useState(true);
  const [perPage, setPerPage] = useState(25);

  const matching = narrowAndSort(accounts.rows, {
    q,
    searchText: (s) => `${s.full_name} ${s.email ?? ""} ${s.phone ?? ""}`,
    keep: (s) => (!role || s.role === role) && (!status || s.status === status),
    sortValue: SORT_VALUE[sort],
    descending,
  });
  const { page, total, hasMore, loadMore } = useClientPages(
    matching,
    perPage,
    JSON.stringify([q, role, status, sort, descending, perPage]),
  );
  const narrowing = (role ? 1 : 0) + (status ? 1 : 0);
  const activeFilters = narrowing + (q.trim() ? 1 : 0);

  const createKey = useIdempotencyKey();
  const create = useMutation({
    mutationFn: (values: StaffFormValues) => createStaff(values, createKey.keyFor(values)),
    onSuccess: (l) => {
      createKey.reset();
      setLink(l);
      setFormError(null);
      setFormOpen(false);
      toast.success(signupLinkSent(l, "Account created. Sign-up link sent"));
      qc.invalidateQueries({ queryKey: ["staff"] });
    },
    onError: (e) => setFormError(describeError(e)),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["staff"] });
  // Each says how it ended (ADM-13): in production they showed nothing, and refusals were dropped.
  const done = (message: string) => () => {
    toast.success(message);
    invalidate();
  };
  const unlock = useMutation({ mutationFn: unlockStaff, onSuccess: done("Account unlocked."), onError: toastFailure });
  const suspend = useMutation({ mutationFn: suspendStaff, onSuccess: done("Account suspended."), onError: toastFailure });
  const activate = useMutation({ mutationFn: activateStaff, onSuccess: done("Account active."), onError: toastFailure });
  const reissue = useMutation({
    mutationFn: reissueSignupLink,
    onSuccess: (l) => {
      setLink(l);
      toast.success(signupLinkSent(l, "New sign-up link sent"));
    },
    onError: toastFailure,
  });

  // Edit and reset password open a form; a refusal shows in it, as on the create form.
  const [editing, setEditing] = useState<StaffAccount | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const edit = useMutation({
    mutationFn: ({ id, values }: { id: string; values: StaffEditValues }) => updateStaff(id, values),
    onSuccess: (s) => {
      setEditing(null);
      invalidate();
      toast.success(`${s.full_name} updated.`);
    },
    onError: (e) => setEditError(describeError(e)),
  });

  const [resetting, setResetting] = useState<StaffAccount | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const resetPassword = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => resetStaffPassword(id, password),
    onSuccess: (s) => {
      setResetting(null);
      invalidate();
      toast.success(`Password reset for ${s.full_name}. They’ll be asked to change it after signing in.`);
    },
    onError: (e) => setResetError(describeError(e)),
  });

  const actions = (s: StaffAccount) => (
    <RowActions className="flex-wrap">
      <IconButton
        label="Edit"
        onClick={() => {
          setEditError(null);
          setEditing(s);
        }}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        label="Reset password"
        onClick={() => {
          setResetError(null);
          setResetting(s);
        }}
      >
        <KeyIcon />
      </IconButton>
      {s.status === "locked" && (
        <IconButton label="Unlock" tone="warning" onClick={() => unlock.mutate(s.id)}>
          <UnlockIcon />
        </IconButton>
      )}
      {s.status === "active" ? (
        <IconButton label="Suspend" tone="danger" onClick={() => suspend.mutate(s.id)}>
          <PauseIcon />
        </IconButton>
      ) : (
        <IconButton label="Activate" tone="success" onClick={() => activate.mutate(s.id)}>
          <PlayIcon />
        </IconButton>
      )}
      {/* A signup link activates the account, so it is never offered for a suspended or
          revoked one (the API refuses it too — ADM-05). */}
      {s.status !== "suspended" && s.status !== "revoked" && (
        <IconButton label="Reissue signup link" onClick={() => reissue.mutate(s.id)}>
          <LinkIcon />
        </IconButton>
      )}
    </RowActions>
  );

  return (
    <div className="flex h-full flex-col gap-3 sm:gap-5">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Staff accounts</h2>
          <p className="text-sm text-gray-500">Pharmacists, admins and support — created with a signup link.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Every account the search and filters match (not just the rows shown), in the table's order. */}
          <ExportButton table="staff accounts" columns={EXPORT_COLUMNS} rows={matching} disabled={accounts.loading} />
          <button
            onClick={() => {
              setFormError(null);
              setFormOpen(true);
            }}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg shadow-xs hover:opacity-90"
          >
            + Add staff
          </button>
        </div>
      </div>

      <Modal open={formOpen} title="Add staff account" size="lg" onClose={() => setFormOpen(false)}>
        <StaffForm
          submitting={create.isPending}
          error={formError}
          onCancel={() => setFormOpen(false)}
          onSubmit={(v) => create.mutate(v)}
        />
      </Modal>

      <Modal open={editing !== null} title="Edit staff account" size="lg" onClose={() => setEditing(null)}>
        {editing && (
          <EditStaffForm
            key={editing.id}
            staff={editing}
            submitting={edit.isPending}
            error={editError}
            onCancel={() => setEditing(null)}
            onSubmit={(values) => edit.mutate({ id: editing.id, values })}
          />
        )}
      </Modal>

      <Modal open={resetting !== null} title="Reset password" onClose={() => setResetting(null)}>
        {resetting && (
          <ResetPasswordForm
            key={resetting.id}
            name={resetting.full_name}
            note={
              resetting.status === "suspended" || resetting.status === "revoked"
                ? `This account is ${STATUS_LABEL[resetting.status].toLowerCase()}: activate it so they can sign in with this password.`
                : undefined
            }
            submitting={resetPassword.isPending}
            error={resetError}
            onCancel={() => setResetting(null)}
            onSubmit={(password) => resetPassword.mutate({ id: resetting.id, password })}
          />
        )}
      </Modal>

      {link?.signup_token_dev && (
        <div className="shrink-0 break-all rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">
          Signup link issued. Dev token: <code className="font-mono">{link.signup_token_dev}</code>
        </div>
      )}

      <FilterBar
        label="Search and filter staff"
        narrowing={narrowing}
        search={<SearchBox value={q} onChange={setQ} placeholder="Search by name, email or phone…" />}
      >
        <SelectFilter label="Role" value={role} options={ROLE_OPTIONS} onChange={setRole} testId="staff-filter-role" />
        <SelectFilter label="Status" value={status} options={STATUS_OPTIONS} onChange={setStatus} testId="staff-filter-status" />
        <SortFilter
          value={sort}
          options={SORTS}
          descending={descending}
          onChange={(s) => {
            setSort(s);
            setDescending(NATURAL_DESC[s]);
          }}
          onToggle={() => setDescending((d) => !d)}
        />
        <BatchSizeFilter value={perPage} onChange={setPerPage} />
        <ClearFilters
          count={activeFilters}
          onClear={() => {
            setQ("");
            setRole("");
            setStatus("");
          }}
        />
      </FilterBar>

      <VirtualTable
        label="Staff accounts"
        columns={COLUMNS}
        rows={page}
        rowKey={(s) => s.id}
        rowHeight={56}
        measureRows
        loading={accounts.loading}
        error={accounts.error ? describeError(accounts.error) : null}
        onRetry={accounts.refetch}
        empty={activeFilters ? "No staff accounts match your filters." : "No staff accounts yet."}
        total={total}
        hasMore={hasMore}
        onLoadMore={loadMore}
        resetKey={JSON.stringify([q, role, status, sort, descending])}
        renderRow={(s) => (
          <TableRow
            columns={COLUMNS}
            className="min-h-14 py-2 text-sm transition-colors hover:bg-gray-50"
            cells={{
              name: (
                <div className="min-w-0">
                  <div className="truncate font-medium text-gray-800">{s.full_name}</div>
                  <div className="truncate text-xs text-gray-400 md:hidden">
                    <span className="sm:hidden">{ROLE_LABEL[s.role] ?? s.role} · </span>
                    {s.email ?? (s.phone ? phoneLabel(s.phone) : "—")}
                  </div>
                </div>
              ),
              role: <span className="text-gray-600">{ROLE_LABEL[s.role] ?? s.role}</span>,
              contact: (
                <div className="min-w-0">
                  <div className="truncate text-gray-600">{s.email ?? "—"}</div>
                  {s.phone && <div className="truncate text-xs text-gray-400">{phoneLabel(s.phone)}</div>}
                </div>
              ),
              status: (
                <span
                  className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    STATUS_TONE[s.status] ?? "bg-gray-100 text-gray-500"
                  }`}
                >
                  {STATUS_LABEL[s.status] ?? s.status.replace(/_/g, " ")}
                </span>
              ),
              actions: actions(s),
            }}
          />
        )}
      />
    </div>
  );
}
